import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Church, Trophy, Settings as SettingsIcon, Share2, Crown, BookOpen, Gamepad2, Book, Shield } from 'lucide-react';
import { getLeaderboard } from '../utils/api';

export default function StartScreen({ user, refreshUser }) {
  const navigate = useNavigate();
  const [userRank, setUserRank] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);

  useEffect(() => {
    // Refresh user data when component loads to get latest total_members
    if (user && refreshUser) {
      refreshUser();
      loadUserRanking();
    }
  }, [user]);

  const loadUserRanking = async () => {
    try {
      const response = await getLeaderboard();
      const leaderboard = response.data.leaderboard || [];
      setTotalPlayers(leaderboard.length);
      
      // Find user's rank by user id
      const userIndex = leaderboard.findIndex(player => player.id === user.id);
      if (userIndex !== -1) {
        setUserRank(userIndex + 1);
      }
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  const handlePlayGame = () => {
    if (user) {
      navigate('/level-select');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden" 
      data-testid="start-screen"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay for better readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/30 via-[#9370DB]/30 to-[#FFD700]/20"></div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl">
        {/* Mega Church Logo with Animation - BIGGER */}
        <div className="flex justify-center mb-8 animate-slow-bounce">
          <img 
            src="https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/9qio0cok_Mega%20Church%20Logo%20T%20Shirt.png"
            alt="Mega Church Logo"
            className="w-80 h-80 sm:w-96 sm:h-96 object-contain drop-shadow-2xl"
            data-testid="mega-church-logo"
          />
        </div>

        {/* White Card with Yellow Border */}
        <Card className="bg-white/95 backdrop-blur-lg border-4 border-yellow-400 shadow-2xl p-8 sm:p-12 rounded-3xl">
          {/* Welcome Message */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4169E1] mb-4" data-testid="welcome-title">
            Welcome to Mega Church
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 mb-8 font-semibold">
            "Build your Mega Church congregation by testing your Bible knowledge."
          </p>

          {/* Action Buttons */}
          <div className="space-y-4 mb-8">
            {!user && (
              <>
                <Button
                  data-testid="play-as-guest-btn"
                  onClick={() => {
                    localStorage.setItem('guest_mode', 'true');
                    navigate('/level-select');
                  }}
                  className="w-full sm:w-96 h-16 text-2xl font-bold bg-gradient-to-r from-[#4169E1] to-[#5B7FE8] text-white hover:from-[#3558c7] hover:to-[#4169E1] rounded-full shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  Play as Guest
                </Button>
                
                <div className="space-y-4 mt-6">
                  <Button
                    data-testid="email-signup-btn"
                    onClick={() => navigate('/auth')}
                    className="w-full sm:w-96 h-14 text-lg font-semibold bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 rounded-full shadow-md"
                  >
                    Sign Up / Login with Email
                  </Button>
                  
                  <p className="text-gray-600 text-sm italic max-w-sm mx-auto mt-4">
                    Login to save your progress and compete on the leaderboard.
                  </p>
                </div>
              </>
            )}

            {user && (
              <>
                <Button
                  data-testid="play-game-btn"
                  onClick={handlePlayGame}
                  className="w-full sm:w-96 h-16 text-2xl font-bold bg-gradient-to-r from-[#4169E1] to-[#5B7FE8] text-white hover:from-[#3558c7] hover:to-[#4169E1] rounded-full shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  Play Game
                </Button>
              </>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="space-y-8 max-w-2xl mx-auto">
            <Button
              data-testid="leaderboard-btn"
              onClick={() => navigate('/leaderboard')}
              variant="outline"
              className="w-full h-14 text-lg font-semibold bg-white text-[#4169E1] border-2 border-[#4169E1] hover:bg-[#4169E1] hover:text-white rounded-2xl"
            >
              <Trophy className="w-5 h-5 mr-2" />
              <span>Leaderboard</span>
            </Button>
            
            <Button
              data-testid="settings-btn"
              onClick={() => navigate('/settings')}
              variant="outline"
              className="w-full h-14 text-lg font-semibold bg-white text-[#4169E1] border-2 border-[#4169E1] hover:bg-[#4169E1] hover:text-white rounded-2xl"
            >
              <SettingsIcon className="w-5 h-5 mr-2" />
              <span>Settings</span>
            </Button>
            
            {user && user.role === 'admin' && (
              <Button
                data-testid="admin-dashboard-btn"
                onClick={() => navigate('/admin')}
                variant="outline"
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white border-2 border-red-600 hover:from-red-600 hover:to-red-700 rounded-2xl shadow-lg"
              >
                <Crown className="w-5 h-5 mr-2" />
                <span>Admin Dashboard</span>
              </Button>
            )}
            
            <Button
              data-testid="share-btn"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Mega Church Game 2025',
                    text: 'Build your Mega Church congregation by testing your Bible knowledge!',
                    url: window.location.href,
                  });
                }
              }}
              variant="outline"
              className="w-full h-14 text-lg font-semibold bg-white text-[#4169E1] border-2 border-[#4169E1] hover:bg-[#4169E1] hover:text-white rounded-2xl"
            >
              <Share2 className="w-5 h-5 mr-2" />
              <span>Share with Friends</span>
            </Button>
            
            <Button
              data-testid="pro-version-btn"
              onClick={() => window.open('https://www.refugeplay.com', '_blank')}
              className="w-full h-16 text-xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 text-white hover:from-yellow-500 hover:to-yellow-700 rounded-2xl shadow-lg transform hover:scale-105 transition-all"
            >
              <Crown className="w-6 h-6 mr-2" />
              <span>Play More Games</span>
            </Button>

            {/* Spiritual Growth Section */}
            <div className="border-t-2 border-white/30 pt-8 mt-8">
              <h3 className="text-[#4169E1] text-center text-2xl font-bold mb-6 flex items-center justify-center gap-2 bg-white/90 py-3 px-6 rounded-2xl shadow-lg">
                <span className="text-3xl">✨</span>
                <span>Spiritual Growth</span>
              </h3>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/bible-study-tips')}
                  variant="outline"
                  className="w-full h-14 text-base font-semibold bg-white/95 text-blue-600 border-2 border-blue-400 hover:bg-blue-50 rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  <span>Bible Study Tips</span>
                </Button>
                
                <Button
                  onClick={() => navigate('/game-strategies')}
                  variant="outline"
                  className="w-full h-14 text-base font-semibold bg-white/95 text-purple-600 border-2 border-purple-400 hover:bg-purple-50 rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <Gamepad2 className="w-5 h-5 mr-2" />
                  <span>Game Strategies</span>
                </Button>
                
                <Button
                  onClick={() => navigate('/scripture-references')}
                  variant="outline"
                  className="w-full h-14 text-base font-semibold bg-white/95 text-green-600 border-2 border-green-400 hover:bg-green-50 rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <Book className="w-5 h-5 mr-2" />
                  <span>Scripture References</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Privacy Policy Link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/privacy-policy')}
              className="text-sm text-gray-600 hover:text-[#4169E1] underline transition-colors"
            >
              Privacy Policy
            </button>
          </div>

          {user && (
            <div className="mt-8 text-gray-700">
              <p className="text-lg">Welcome back, <span className="font-bold text-[#4169E1]">{user.name}</span>!</p>
              <div className="space-y-2">
                <p className="text-sm">Current Run: <span className="font-bold text-[#4169E1] text-xl">{user.current_run_members || user.total_members}</span> members</p>
                <p className="text-sm">Best Score: <span className="font-bold text-green-600 text-xl">{user.best_score}</span> members</p>
                <p className="text-xs text-gray-500">Round {user.current_round || 1}</p>
              </div>
              {userRank && (
                <div className="mt-6 flex justify-center">
                  <div className="w-full max-w-md bg-gradient-to-r from-[#4169E1] to-[#5B7FE8] rounded-2xl shadow-lg p-6 animate-pulse-slow">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <Trophy className="w-7 h-7 text-white" />
                      <p className="text-xl font-bold text-white">
                        Leaderboard Rank: <span className="text-2xl font-black">#{userRank}</span> of {totalPlayers}
                      </p>
                    </div>
                    <p className="text-center text-sm font-semibold text-white/90 italic">
                      Keep Playing To Move Up
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

        </Card>
      </div>
    </div>
  );
}
